import { db } from './db.js'
import { supabase } from './supabase.js'
import { addAuditLog } from './audit.js'
import { drawPDFHeader, drawTableHeader, drawTableRow, checkNewPage } from './pdfUtils.js'
import { jsPDF } from 'jspdf'
import { createCompraPendente, updateCompraPendente, deleteCompraPendente, fetchComprasPendentes, fetchHistoricoPedidos, getNextOrderNumber, createHistoricoPedido, moverCompraParaHistorico, getEmpresaId } from './syncManager.js'



export async function getActiveOrder() {

  const ativos = await db.pedidosAtivo.toArray()

  return ativos[0] ?? null

}



export async function startNewOrder() {

  const ativo = await getActiveOrder()

  if (ativo) throw new Error('Já existe um pedido ativo. Finalize ou cancele-o primeiro.')



  const order = {

    id: 'ativo',

    dataInicio: Date.now(),

    itens: []

  }

  await db.pedidosAtivo.add(order)

  return order

}



export async function addItemToOrder({ produtoId, produtoNome, tipo, quantidadeSolicitada, observacao, urgencia = 'normal', modo = 'estoque' }) {
  const ativo = await getActiveOrder()
  if (!ativo) throw new Error('Nenhum pedido ativo. Inicie um novo pedido.')

  const item = {
    id: crypto.randomUUID(),
    produtoId,
    produtoNome,
    tipo,
    quantidadeSolicitada: Number(quantidadeSolicitada) || 0,
    observacao: observacao ?? null,
    urgencia,
    modo,
    adicionadoEm: Date.now()
  }



  ativo.itens = [...(ativo.itens || []), item]

  await db.pedidosAtivo.update(ativo.id, { itens: ativo.itens })

  return ativo

}



export async function removeItemFromOrder(itemId) {

  const ativo = await getActiveOrder()

  if (!ativo) throw new Error('Nenhum pedido ativo.')



  ativo.itens = (ativo.itens || []).filter(i => i.id !== itemId)

  await db.pedidosAtivo.update(ativo.id, { itens: ativo.itens })

  return ativo

}



export async function finalizeOrder({ usuario, numeroPedido }) {

  const ativo = await getActiveOrder()

  if (!ativo) throw new Error('Nenhum pedido ativo.')

  if (!Array.isArray(ativo.itens) || ativo.itens.length === 0) throw new Error('Pedido vazio.')



  const pedidoFinal = {

    id: ativo.id,

    numero: numeroPedido || `PED-${Date.now()}`,

    dataInicio: ativo.dataInicio,

    dataEnvio: Date.now(),

    itens: ativo.itens,

    usuarioId: usuario?.usuarioId ?? null,

    usuarioNome: usuario?.usuarioNome ?? null

  }



  await db.pedidosArquivados.add(pedidoFinal)

  await db.pedidosAtivo.delete(ativo.id)



  await addAuditLog({

    usuarioId: usuario?.usuarioId ?? null,

    usuarioNome: usuario?.usuarioNome ?? null,

    acao: 'PEDIDO_FINALIZADO',

    detalhes: JSON.stringify({ pedidoId: pedidoFinal.id, numero: pedidoFinal.numero, itensCount: pedidoFinal.itens.length }),

    tabelaAfetada: 'pedidosArquivados',

    registroId: pedidoFinal.id

  })



  return pedidoFinal

}



export async function cancelActiveOrder({ usuario }) {

  const ativo = await getActiveOrder()

  if (!ativo) return null



  await db.pedidosAtivo.delete(ativo.id)



  await addAuditLog({

    usuarioId: usuario?.usuarioId ?? null,

    usuarioNome: usuario?.usuarioNome ?? null,

    acao: 'PEDIDO_CANCELADO',

    detalhes: JSON.stringify({ pedidoId: ativo.id }),

    tabelaAfetada: 'pedidosAtivo',

    registroId: ativo.id

  })



  return true

}



export async function listArchivedOrders({ limit = 50 } = {}) {

  return db.pedidosArquivados

    .orderBy('dataEnvio')

    .reverse()

    .limit(limit)

    .toArray()

}



export async function deleteArchivedOrder(orderId) {

  const pedido = await db.pedidosArquivados.get(orderId)

  if (!pedido) throw new Error('Pedido não encontrado.')

  

  await db.pedidosArquivados.delete(orderId)

  return pedido

}



export function generateOrderText(pedido) {

  const dataStr = new Date(pedido.dataEnvio).toLocaleDateString('pt-BR')

  let txt = `*Pedido: ${pedido.numero}*\n📅 Data: ${dataStr}\n\n*Itens solicitados:*\n`



  pedido.itens.forEach((it, idx) => {

    txt += `${idx + 1}. ${it.produtoNome} (${it.tipo}) - Qtd: ${it.quantidadeSolicitada}${it.observacao ? ` - Obs: ${it.observacao}` : ''}\n`

  })



  txt += `\n_Total: ${pedido.itens.length} itens_`

  return txt

}



export function shareViaWhatsApp(text) {

  const encoded = encodeURIComponent(text)

  const url = `https://wa.me/?text=${encoded}`

  window.open(url, '_blank')

}

export async function generateOrderPDF(pedido, userName = 'Sistema', returnBlob = false) {
  try {
    const doc = new jsPDF('landscape')

    await drawPDFHeader(doc, {
      titulo: 'PEDIDO DE COMPRA',
      subtitulo: `Nº ${pedido.numero}`,
      usuario: userName,
      infoExtra: `Itens: ${pedido.itens.length}`,
      landscape: true
    })

    let y = 75
    const colWidths = [15, 125, 40, 25, 55]
    const headers = ['#', 'Produto', 'Tipo', 'Qtd', 'Obs']
    drawTableHeader(doc, headers, y, colWidths, 'center')
    y += 10

    doc.setFontSize(8)
    pedido.itens.forEach((it, idx) => {
      y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
      drawTableRow(doc, [
        String(idx + 1),
        it.produtoNome,
        it.tipo,
        String(it.quantidadeSolicitada),
        it.observacao || '-'
      ], y, colWidths, 'center')
      y += 7
    })

    if (returnBlob) {
      return doc.output('blob')
    } else {
      doc.save(`pedido-${pedido.numero}.pdf`)
    }
  } catch (err) {
    console.error('Erro ao gerar PDF do pedido:', err)
    throw new Error(
      err?.message?.includes('jsPDF') ?
        'Biblioteca PDF não disponível. Tente recarregar a página.' :
        err?.message?.includes('fetch') ?
        'Erro ao carregar recursos. Verifique sua conexão.' :
        `Erro ao gerar PDF: ${err?.message || 'Erro desconhecido'}`
    )
  }
}



export async function shareOrderViaWhatsApp(pedido, userName = 'Sistema') {

  try {

    // Gerar o PDF como blob

    const pdfBlob = await generateOrderPDF(pedido, userName, true)

    const file = new File([pdfBlob], `pedido-${pedido.numero}.pdf`, { type: 'application/pdf' })



    // Tentar usar Web Share API (funciona em mobile moderno)

    if (navigator.canShare && navigator.canShare({ files: [file] })) {

      await navigator.share({

        title: `Pedido ${pedido.numero}`,

        text: `Pedido de compra Nº ${pedido.numero} com ${pedido.itens.length} itens.`,

        files: [file]

      })

    } else {

      // Fallback: baixar PDF

      await generateOrderPDF(pedido, userName, false)

      alert('PDF baixado. Por favor, anexe-o manualmente no WhatsApp.')

    }

  } catch (err) {

    console.error('Erro ao compartilhar:', err)

    // Fallback: baixar PDF

    await generateOrderPDF(pedido, userName, false)

    alert('PDF baixado. Por favor, anexe-o manualmente no WhatsApp.')

  }

}

// ============================
// Funções do Módulo de Compras
// ============================

export async function sendOrderToCompras({ usuario, numeroPedido }) {
  const ativo = await getActiveOrder()
  if (!ativo || !ativo.itens || ativo.itens.length === 0) {
    throw new Error('Não há itens no pedido para enviar à compras.')
  }

  const numero = numeroPedido || ativo.numero || (await getNextOrderNumber())

  const compra = {
    id: crypto.randomUUID(),
    numero,
    dataEnvio: Date.now(),
    usuarioId: usuario?.usuarioId ?? usuario?.id ?? null,
    usuarioNome: usuario?.usuarioNome ?? usuario?.nome ?? 'Sistema',
    status: 'aguardando',
    itens: ativo.itens.map(item => ({
      ...item,
      id: item.id || crypto.randomUUID(),
      status: 'aguardando',
      quantidadeComprada: null,
      dataConfirmacao: null
    }))
  }

  // Salvar diretamente no Supabase (CRUD online-first)
  const compraCriada = await createCompraPendente(compra)

  // NÃO criar histórico aqui - histórico só é criado após confirmação/rejeição do comprador

  // Deletar pedido ativo local
  await db.pedidosAtivo.delete('ativo')

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? usuario?.id ?? null,
    usuarioNome: usuario?.usuarioNome ?? usuario?.nome ?? null,
    acao: 'ENVIAR_PARA_COMPRAS',
    tabelaAfetada: 'comprasPendentes',
    registroId: compraCriada.id,
    detalhes: { numero, quantidadeItens: ativo.itens.length }
  })

  return { compra: compraCriada }
}

export async function listHistoricoPedidos({ limit = 50, force = false } = {}) {
  console.log('[ONLINE] Buscando histórico de pedidos do Supabase...')
  const historico = await fetchHistoricoPedidos(force)
  // Ordenar por criadoEm e limitar
  return historico
    .sort((a, b) => (b.criadoEm || 0) - (a.criadoEm || 0))
    .slice(0, limit)
}

export async function deleteHistoricoPedido(pedidoId) {
  // Deletar do IndexedDB local
  const pedido = await db.historicoPedidos.get(pedidoId)
  if (pedido) {
    await db.historicoPedidos.delete(pedidoId)
  }
  
  // Deletar do Supabase
  try {
    const { deleteRecord } = await import('./syncManager.js')
    await deleteRecord('historico_pedidos', pedidoId)
  } catch (err) {
    console.warn('[deleteHistoricoPedido] Erro ao deletar do Supabase:', err)
  }
  
  return pedido
}

export async function listComprasPendentes({ limit = 50, force = false } = {}) {
  console.log('[ONLINE] Buscando compras pendentes do Supabase...')
  const compras = await fetchComprasPendentes(force)
  // Ordenar por dataEnvio e limitar
  return compras
    .sort((a, b) => (b.dataEnvio || 0) - (a.dataEnvio || 0))
    .slice(0, limit)
}

export async function confirmarCompra({ pedidoId, itemId, quantidadeComprada, usuario }) {
  console.log('[ONLINE] Confirmando compra:', pedidoId, itemId)
  
  // Buscar pedido do Supabase
  const compras = await fetchComprasPendentes()
  const pedido = compras.find(p => p.id === pedidoId)
  if (!pedido) throw new Error('Pedido não encontrado.')

  const item = pedido.itens.find(i => i.id === itemId)
  if (!item) throw new Error('Item não encontrado no pedido.')

  // Atualizar item
  const itensAtualizados = pedido.itens.map(i => {
    if (i.id === itemId) {
      return {
        ...i,
        quantidadeComprada: quantidadeComprada || i.quantidadeSolicitada,
        status: quantidadeComprada && quantidadeComprada < i.quantidadeSolicitada ? 'parcial' : 'concluido',
        dataConfirmacao: Date.now(),
        usuarioConfirmacaoId: usuario?.usuarioId ?? usuario?.id ?? null,
        usuarioConfirmacaoNome: usuario?.usuarioNome ?? usuario?.nome ?? 'Sistema'
      }
    }
    return i
  })

  // Calcular novo status do pedido
  const todosConcluidos = itensAtualizados.every(i => i.status === 'concluido')
  const algumConcluido = itensAtualizados.some(i => i.status === 'concluido' || i.status === 'parcial')

  const novoStatus = todosConcluidos ? 'comprado' : algumConcluido ? 'parcial' : pedido.status

  // Atualizar no Supabase
  const pedidoAtualizado = await updateCompraPendente(pedidoId, {
    itens: itensAtualizados,
    status: novoStatus
  })

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? usuario?.id ?? null,
    acao: 'CONFIRMAR_COMPRA',
    tabelaAfetada: 'comprasPendentes',
    registroId: pedidoId,
    detalhes: { itemId, quantidadeComprada: itensAtualizados.find(i => i.id === itemId).quantidadeComprada }
  })

  return { pedido: pedidoAtualizado, item: itensAtualizados.find(i => i.id === itemId) }
}

export async function rejeitarItemCompra({ pedidoId, itemId, motivo, usuario }) {
  console.log('[ONLINE] Rejeitando item compra:', pedidoId, itemId)

  // Buscar pedido do Supabase
  const compras = await fetchComprasPendentes()
  const pedido = compras.find(p => p.id === pedidoId)
  if (!pedido) throw new Error('Pedido de compra não encontrado.')

  const itemRejeitado = pedido.itens.find(i => i.id === itemId)
  if (!itemRejeitado) throw new Error('Item não encontrado no pedido.')

  // Remover item do pedido
  const itensAtualizados = pedido.itens.filter(i => i.id !== itemId)

  // Se não houver mais itens, deletar o pedido e mover para histórico
  if (itensAtualizados.length === 0) {
    await deleteCompraPendente(pedidoId)
    // Criar histórico com status rejeitado
    const historicoSnake = {
      id: crypto.randomUUID(),
      numero: pedido.numero,
      usuario_id: pedido.usuarioId || null,
      empresa_id: getEmpresaId(),
      itens: pedido.itens,
      data_envio: pedido.dataEnvio || Date.now(),
      finalizado_em: Date.now(),
      dados: { 
        status: 'rejeitado', 
        motivo, 
        itemRejeitado: itemRejeitado.produtoNome,
        usuarioConfirmacaoNome: usuario?.usuarioNome ?? usuario?.nome ?? 'Sistema'
      }
    }
    await supabase.from('historico_pedidos').insert(historicoSnake)
  } else {
    // Atualizar pedido no Supabase
    await updateCompraPendente(pedidoId, { itens: itensAtualizados })
  }

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? usuario?.id ?? null,
    usuarioNome: usuario?.usuarioNome ?? usuario?.nome ?? null,
    acao: 'REJEITAR_ITEM_COMPRA',
    tabelaAfetada: 'comprasPendentes',
    registroId: pedidoId,
    detalhes: { itemId, produtoNome: itemRejeitado.produtoNome, motivo }
  })

  return { pedido: { ...pedido, itens: itensAtualizados }, itemRejeitado }
}

export async function moverParaHistoricoCompras(pedidoId) {
  console.log('[ONLINE] Movendo compra para histórico:', pedidoId)
  return moverCompraParaHistorico(pedidoId)
}

export async function listHistoricoCompras({ limit = 100 } = {}) {
  console.log('[ONLINE] Buscando histórico de compras do Supabase...')
  const historico = await fetchHistoricoPedidos()
  // Ordenar por criadoEm e limitar
  return historico
    .sort((a, b) => (b.criadoEm || 0) - (a.criadoEm || 0))
    .slice(0, limit)
}

